/**
 * O renderizador WebGL do orbe: compila o shader uma vez e desenha um triângulo que cobre o
 * canvas. Criado uma vez por montagem do `OrbeDaVecchia`; depois de uma perda de contexto, o
 * componente chama de novo sobre o mesmo canvas.
 */

import { FRAGMENTO, VERTICE } from './shader';
import type { ParametrosDoOrbe } from './sinal';

export interface OrbeWebGL {
    desenhar: (parametros: ParametrosDoOrbe, tempoS: number, relogioS: number) => void;
    /** Solta programa, shaders e buffer. */
    destruir: () => void;
}

/**
 * `sem-webgl`: o navegador não deu contexto (o canvas 2D ainda pode ser usado).
 * `falhou`: deu contexto, mas o shader não compilou — o canvas já está preso ao WebGL.
 */
export type ResultadoDoWebGL = { tipo: 'pronto'; orbe: OrbeWebGL } | { tipo: 'sem-webgl' } | { tipo: 'falhou' };

/** Os uniformes que vêm direto de `ParametrosDoOrbe`, com o nome do campo. */
const UNIFORMES_DOS_PARAMETROS: [string, keyof ParametrosDoOrbe][] = [
    ['u_deformacao', 'deformacao'],
    ['u_fluxo', 'fluxo'],
    ['u_ondulacao', 'ondulacao'],
    ['u_energia', 'energia'],
    ['u_brilho', 'brilho'],
    ['u_recolher', 'recolher'],
    ['u_aura', 'aura'],
    ['u_abertura', 'abertura'],
    ['u_halo', 'halo'],
];

function compilar(gl: WebGLRenderingContext, tipo: number, fonte: string): WebGLShader | null {
    const shader = gl.createShader(tipo);
    if (!shader) return null;
    gl.shaderSource(shader, fonte);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.warn('Orbe da VecchIA: o shader não compilou.', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

export function criarOrbeWebGL(canvas: HTMLCanvasElement): ResultadoDoWebGL {
    let contexto: WebGLRenderingContext | null = null;
    try {
        contexto = canvas.getContext('webgl', {
            alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power',
        });
    } catch {
        contexto = null;
    }
    if (!contexto) return { tipo: 'sem-webgl' };
    const gl = contexto;
    if (gl.isContextLost()) return { tipo: 'falhou' };

    const vertice = compilar(gl, gl.VERTEX_SHADER, VERTICE);
    const fragmento = compilar(gl, gl.FRAGMENT_SHADER, FRAGMENTO);
    const programa = gl.createProgram();
    if (!vertice || !fragmento || !programa) return { tipo: 'falhou' };
    gl.attachShader(programa, vertice);
    gl.attachShader(programa, fragmento);
    gl.linkProgram(programa);
    if (!gl.getProgramParameter(programa, gl.LINK_STATUS)) {
        console.warn('Orbe da VecchIA: o programa WebGL não ligou.', gl.getProgramInfoLog(programa));
        return { tipo: 'falhou' };
    }

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    /* Um triângulo só, maior que a tela, cobre o canvas inteiro. */
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.useProgram(programa);
    const posicao = gl.getAttribLocation(programa, 'a_pos');
    gl.enableVertexAttribArray(posicao);
    gl.vertexAttribPointer(posicao, 2, gl.FLOAT, false, 0, 0);

    const uTela = gl.getUniformLocation(programa, 'u_tela');
    const uTempo = gl.getUniformLocation(programa, 'u_tempo');
    const uRelogio = gl.getUniformLocation(programa, 'u_relogio');
    const dosParametros = UNIFORMES_DOS_PARAMETROS.map(([nome, campo]) => ({ local: gl.getUniformLocation(programa, nome), campo }));

    return {
        tipo: 'pronto',
        orbe: {
            desenhar(parametros, tempoS, relogioS) {
                if (gl.isContextLost()) return;
                const largura = gl.drawingBufferWidth;
                const altura = gl.drawingBufferHeight;
                gl.viewport(0, 0, largura, altura);
                gl.clearColor(0, 0, 0, 0);
                gl.clear(gl.COLOR_BUFFER_BIT);
                gl.uniform2f(uTela, largura, altura);
                gl.uniform1f(uTempo, tempoS);
                gl.uniform1f(uRelogio, relogioS);
                for (const u of dosParametros) gl.uniform1f(u.local, parametros[u.campo]);
                gl.drawArrays(gl.TRIANGLES, 0, 3);
            },
            destruir() {
                if (gl.isContextLost()) return;
                gl.deleteBuffer(buffer);
                gl.deleteProgram(programa);
                gl.deleteShader(vertice);
                gl.deleteShader(fragmento);
            },
        },
    };
}
